import React, { createContext, useContext, useState, useEffect } from 'react';
import pb from '@/lib/pocketbaseClient.js';

const EditContext = createContext();

export const useEditContext = () => useContext(EditContext);

const initialContent = {
  hero: {
    badge: "⏰ OFERTA VÁLIDA APENAS HOJE",
    title: "🔥 Aumente Seus Lucros em 40% em 30 Dias",
    subtitle: "(Ou Devolvemos 100% do Seu Dinheiro)",
    description: "Descubra os produtos congelados premium que já multiplicaram o faturamento de mais de 500 padarias e confeitarias no estado. Margem de 35-50% e entrega em 24h garantida.",
    ctaText: "🚀 FALE COM NOSSO COMERCIAL AGORA",
    ctaMicroCopy: "Resposta em menos de 5 minutos | Sem compromisso | Consultoria Grátis"
  },
  portfolio: {
    title: "Produtos Que Vendem Sozinhos - Margem de 35-50%",
    subtitle: "O mix ideal para atrair clientes todos os dias e aumentar o ticket médio.",
    products: [
      {
        title: "Pão Francês Artesanal",
        badge: "🔥 MAIS VENDIDO",
        image: "https://images.unsplash.com/photo-1530006112272-ae00f8ab6db0",
        margem: "40%",
        rotatividade: "3-4x/semana",
        beneficio: "Padarias ganham R$ 500-800/semana",
        exemplo: "João Silva: +45% nas vendas",
        ctaText: "SOLICITAR AMOSTRA GRÁTIS"
      },
      {
        title: "Pão de Queijo Premium",
        badge: "⭐ FAVORITO DOS CLIENTES",
        image: "https://images.unsplash.com/photo-1703219659668-8672a0ff8125",
        margem: "45%",
        rotatividade: "Diária",
        beneficio: "Alta compra por impulso no caixa",
        exemplo: "Maria Santos triplicou vendas",
        ctaText: "SOLICITAR AMOSTRA GRÁTIS"
      },
      {
        title: "Doces Finos Decorados",
        badge: "💎 PRODUTO PREMIUM",
        image: "https://images.unsplash.com/photo-1661902339263-685cc8189c86",
        margem: "50%",
        rotatividade: "Fim de Semana",
        beneficio: "Aumenta o ticket médio em 20%",
        exemplo: "Carlos Oliveira +35% no lucro",
        ctaText: "SOLICITAR AMOSTRA GRÁTIS"
      }
    ]
  },
  benefits: {
    title: "Soluções Personalizadas Para Seu Tipo de Negócio",
    cards: [
      { title: "Padarias", icon: "Wheat", bgClass: "bg-[#Fdfbf7]", problem: "Alta concorrência e margens apertadas na panificação.", result: "Aumentam faturamento em 35-45%", ctaText: "Falar com Especialista em Padarias", benefits: ["Pães que não murcham na vitrine", "Sem necessidade de padeiro noturno", "Fornadas perfeitas a qualquer hora", "Variedade para atrair novos clientes", "Redução drástica de desperdício"] },
      { title: "Confeitarias", icon: "Cake", bgClass: "bg-[#fcf5f7]", problem: "Precisam de produtos premium diferenciados com constância.", result: "Aumentam ticket médio em 60%", ctaText: "Conhecer Catálogo de Doces", benefits: ["Doces de alto padrão visual", "Ingredientes de primeira linha", "Pronto para finalizar e decorar", "Longa validade congelado", "Embalagens protetoras na entrega"] },
      { title: "Restaurantes", icon: "UtensilsCrossed", bgClass: "bg-[#f0f7f9]", problem: "Precisam de fornecimento confiável e padronizado.", result: "99.8% de satisfação dos clientes", ctaText: "Solicitar Proposta Personalizada", benefits: ["Entradas que valorizam o prato", "Sobremesas prontas de alto nível", "Entrega 100% pontual", "Foco no prato principal", "Facilidade de armazenamento"] },
      { title: "Cafeterias", icon: "Coffee", bgClass: "bg-[#f5f1ec]", problem: "Precisam de produtos que complementem o café rápido.", result: "Aumentam acompanhamentos em 50%", ctaText: "Explorar Produtos Para Cafeterias", benefits: ["Salgados rápidos de forno", "Pão de queijo premium", "Doces porcionados ideais", "Estoque otimizado em pouco espaço", "Preparo simplificado na estufa"] }
    ]
  },
  comparison: {
    title: "Por Que Hiperpan é Diferente",
    subtitle: "Compare os números frios e entenda onde está seu lucro.",
    rows: [
      { criteria: "Entrega", hiperpan: "24h Garantida", highlight: true },
      { criteria: "Qualidade", hiperpan: "Premium/Padronizado", highlight: false },
      { criteria: "Margem", hiperpan: "35-50%", highlight: true },
      { criteria: "Suporte", hiperpan: "24/7 VIP", highlight: false },
      { criteria: "Variedade", hiperpan: "200+ Itens", highlight: false },
      { criteria: "Garantia", hiperpan: "100% Reembolso", highlight: true },
      { criteria: "Frete Grátis", hiperpan: "Sim (> R$500, todo o RS)", highlight: false }
    ],
    ctaText: "COMEÇAR COM HIPERPAN AGORA"
  },
  faq: {
    title: "Dúvidas Frequentes",
    subtitle: "Tudo o que você precisa saber antes de fazer seu primeiro pedido."
  }
};

export const EditProvider = ({ children }) => {
  const [editMode, setEditMode] = useState(false);
  const [content, setContent] = useState(initialContent);
  const [isAdmin, setIsAdmin] = useState(pb.authStore.model?.role === 'admin');

  useEffect(() => {
    const saved = localStorage.getItem('hiperpan_content');
    if (saved) {
      try {
        setContent(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse saved content", e);
      }
    }

    // Listen to Auth state changes to lock out users who log out or change role
    const unsubscribe = pb.authStore.onChange((token, model) => {
      const adminStatus = model?.role === 'admin';
      setIsAdmin(adminStatus);
      if (!adminStatus) {
        setEditMode(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const toggleEditMode = () => {
    if (isAdmin) {
      setEditMode(!editMode);
    }
  };

  const updateContent = (section, field, value) => {
    setContent(prev => {
      const keys = field.split('.');
      if (keys.length === 1) {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [field]: value
          }
        };
      }

      // Deep clone the specific section to update nested paths like "cards.0.title"
      const newSection = JSON.parse(JSON.stringify(prev[section]));
      let current = newSection;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return {
        ...prev,
        [section]: newSection
      };
    });
  };

  const saveContent = () => {
    localStorage.setItem('hiperpan_content', JSON.stringify(content));
    setEditMode(false);
  };

  const resetContent = () => {
    setContent(initialContent);
    localStorage.removeItem('hiperpan_content');
  };

  return (
    <EditContext.Provider value={{ isAdmin, editMode, toggleEditMode, content, updateContent, saveContent, resetContent }}>
      {children}
    </EditContext.Provider>
  );
};